import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { LogOut, CheckCircle2, User as UserIcon } from 'lucide-react'
import { getGoogleUser, signOutGoogle, type GoogleUser } from '../lib/googleAuth'
import { trackEvent } from '../lib/tracking'

/**
 * Sticky top-right signed-in badge. Shows avatar + name once a Google user
 * is in localStorage. Pops a green "Signed in" toast the first time we see
 * a freshly completed OAuth callback.
 */
export default function UserBadge() {
  const [user, setUser] = useState<GoogleUser | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const u = getGoogleUser()
    if (u) setUser(u)

    // Detect a brand-new login (the App.tsx handler removed ?google_auth= already,
    // but we can check sessionStorage for a "fresh" flag it sets)
    const fresh = sessionStorage.getItem('cs_fresh_login')
    if (fresh && u) {
      sessionStorage.removeItem('cs_fresh_login')
      setShowToast(true)
      setTimeout(() => setShowToast(false), 4000)
    }

    // Poll localStorage in case another tab signs in/out
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'cs_user') setUser(getGoogleUser())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const handleSignOut = () => {
    trackEvent('user_signout', { email: user?.email })
    signOutGoogle()
    setUser(null)
    setMenuOpen(false)
  }

  return (
    <>
      {/* Success toast */}
      <AnimatePresence>
        {showToast && user && (
          <motion.div
            initial={{ opacity: 0, y: -40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -40, scale: 0.9 }}
            className="fixed top-4 left-1/2 z-[100] -translate-x-1/2"
          >
            <div className="flex items-center gap-3 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 backdrop-blur-xl shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              <div className="text-sm">
                <span className="text-emerald-300">Signed in as</span>{' '}
                <span className="font-semibold text-white">{user.name || user.email}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Badge (fixed top right) */}
      <AnimatePresence>
        {user && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-4 right-4 z-40"
          >
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 py-1.5 pl-1.5 pr-4 backdrop-blur-xl hover:bg-white/10 transition shadow-lg"
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  referrerPolicy="no-referrer"
                  className="h-8 w-8 rounded-full border-2 border-white/20 object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-cosmic">
                  <UserIcon className="h-4 w-4 text-white" />
                </div>
              )}
              <div className="text-left min-w-0 hidden sm:block">
                <div className="text-xs font-semibold text-white leading-tight truncate max-w-[140px]">
                  {user.name || user.email.split('@')[0]}
                </div>
                <div className="text-[10px] text-white/50 leading-tight truncate max-w-[140px]">
                  {user.email}
                </div>
              </div>
            </button>

            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-white/10 bg-void-black/95 backdrop-blur-xl shadow-2xl"
                >
                  <div className="border-b border-white/5 p-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          referrerPolicy="no-referrer"
                          className="h-11 w-11 rounded-full border-2 border-white/20"
                        />
                      ) : (
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-cosmic">
                          <UserIcon className="h-5 w-5 text-white" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-white">
                          {user.name || 'User'}
                        </div>
                        <div className="truncate text-xs text-white/50">{user.email}</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 px-4 py-3 text-sm text-white/70 hover:bg-white/5 hover:text-white transition"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
